/**
 * Centre de Commande HAL 3000
 * Dashboard unifié - OPTIMISÉ POUR IPAD
 * 
 * Layout 4 colonnes (plus lisible):
 * 1. TEMPÉRATURES 
 * 2. ÉCLAIRAGE + CHAUFFAGE
 * 3. ÉNERGIE + VÉHICULES
 * 4. SÉCURITÉ
 */
import { 
  Thermometer, Lightbulb, Flame, Droplets, Sun, Zap, 
  AlertTriangle, Power, PowerOff,
  RefreshCw, TrendingUp, TrendingDown, 
  BatteryLow, CloudRain, ChevronRight, Waves, Mail,
  Car, Bike, Battery, Lock, MapPin, Shield, Home
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { useAuth } from '@/components/AuthContext';

const getTempColor = (temp) => {
  if (temp === null || temp === undefined) return '#666';
  if (temp < 12) return '#3b82f6';
  if (temp < 16) return '#22c55e';
  if (temp <= 25) return '#f97316';
  return '#ef4444';
};

const SectionHeader = ({ color, label, onClick }) => (
  <div className="flex items-center justify-between mb-2">
    <span className="text-sm font-mono font-bold tracking-wider" style={{ color, textShadow: `0 0 10px ${color}50` }}>
      {label}
    </span>
    {onClick && (
      <button onClick={onClick} className="text-xs font-mono flex items-center opacity-70 hover:opacity-100" style={{ color }}>
        Détails <ChevronRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

const BigDataWidget = ({ icon: Icon, title, value, unit, color, subtitle, onClick }) => (
  <div 
    onClick={onClick}
    className={`p-3 rounded-lg border transition-all ${onClick ? 'cursor-pointer hover:brightness-110' : ''}`}
    style={{ background: `${color}10`, borderColor: `${color}40` }}
  >
    <div className="flex items-center justify-between mb-1">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-xs font-mono text-gray-400">{title}</span>
    </div>
    <div className="text-2xl font-bold font-mono" style={{ color, textShadow: `0 0 15px ${color}50` }}>
      {value}<span className="text-sm ml-1 opacity-70">{unit}</span>
    </div>
    {subtitle && <div className="text-xs text-gray-500 font-mono mt-1">{subtitle}</div>}
  </div>
);

const TempRow = ({ name, temp, humidity, battery, online }) => (
  <div className={`flex items-center justify-between py-1.5 px-2 rounded ${online ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: online ? getTempColor(temp) : '#444' }} />
      <span className="text-sm font-mono text-gray-300 truncate">{name.replace(/Capteur.*/i, '').replace(/température/i, '').trim()}</span>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {humidity !== null && <span className="text-xs text-cyan-500/70 font-mono">{humidity}%</span>}
      <span className="text-base font-bold font-mono" style={{ color: getTempColor(temp) }}>{temp?.toFixed(1) || '--'}°</span>
      {battery !== null && battery < 20 && <BatteryLow className="w-3 h-3 text-red-400 animate-pulse" />}
    </div>
  </div>
);

const VehicleWidget = ({ vehicle, type }) => {
  const data = vehicle?.data;
  const Icon = type === 'mg4' ? Car : Bike;
  const color = type === 'mg4' ? '#3b82f6' : '#10b981';
  const name = type === 'mg4' ? 'MG4 Electric' : 'NIU RQi 125';
  
  if (!vehicle?.connected) {
    return (
      <div className="p-2 rounded border border-dashed flex items-center gap-2" style={{ borderColor: `${color}30`, background: `${color}05` }}>
        <Icon className="w-5 h-5" style={{ color: `${color}50` }} />
        <div className="flex-1">
          <div className="text-sm font-mono font-bold" style={{ color: `${color}70` }}>{name}</div>
          <div className="text-xs font-mono text-gray-600">{vehicle?.token_required ? 'Token requis' : 'Non connecté'}</div>
        </div>
      </div>
    );
  }
  
  const batteryPct = data?.battery_pct;
  const rangeKm = data?.range_km;
  
  return (
    <div className="p-2 rounded border" style={{ borderColor: `${color}40`, background: `${color}08` }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color }} />
          <span className="text-sm font-mono font-bold" style={{ color }}>{name}</span>
        </div>
        <div className="flex items-center gap-1">
          {data?.locked && <Lock className="w-3 h-3 text-green-400" />}
          {data?.charging && <Zap className="w-3 h-3 text-green-400 animate-pulse" />}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <Battery className="w-4 h-4" style={{ color: batteryPct > 50 ? '#22c55e' : batteryPct > 20 ? '#f97316' : '#ef4444' }} />
          <span className="text-xl font-bold font-mono" style={{ color }}>{batteryPct?.toFixed(0) || '--'}%</span>
        </div>
        <span className="text-sm font-mono text-gray-400">{rangeKm || '--'} km</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [lights, setLights] = useState({ on: 0, total: 0 });
  const [heating, setHeating] = useState(null);
  const [solar, setSolar] = useState(null);
  const [grid, setGrid] = useState(null);
  const [smokeDetectors, setSmokeDetectors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [mailbox, setMailbox] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [evCharger, setEvCharger] = useState(null);
  const [homeConnect, setHomeConnect] = useState(null);
  const [ariston, setAriston] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get('/integrations/tuya/devices'),
        api.get('/integrations/vicare/status'),
        api.get('/integrations/solar/realtime'),
        api.get('/integrations/enedis/summary'),
        api.get('/weather'),
        api.get('/mailbox/status'),
        api.get('/integrations/vehicles/status'),
        api.get('/integrations/vehicles/charger/status'),
        api.get('/integrations/homeconnect/appliances').catch(() => ({ data: null })),
        api.get('/integrations/ariston/status').catch(() => ({ data: null })),
      ]);

      const [devicesRes, vicareRes, solarRes, enedisRes, weatherRes, mailboxRes, vehiclesRes, chargerRes, homeConnectRes, aristonRes] = results;

      // Tuya devices
      if (devicesRes.status === 'fulfilled' && devicesRes.value.data?.success) {
        const devices = devicesRes.value.data.devices || [];
        
        // IMPORTANT: Filtrer par SAINT-GEORGES uniquement (owner_id)
        const SAINT_GEORGES_OWNER_ID = 26773026;
        const saintGeorgesDevices = devices.filter(d => Number(d.owner_id) === SAINT_GEORGES_OWNER_ID);
        
        // Temperature sensors - UNIQUEMENT Saint-Georges
        const tempSensors = saintGeorgesDevices
          .filter(d => d.category === 'wsdcg')
          .filter(d => {
            // Exclure atelier/garage pour moyenne (c'est la temp extérieure)
            const name = (d.name || '').toLowerCase();
            return !name.includes('atelier') && !name.includes('garage');
          })
          .map(d => {
            const status = {};
            (d.status || []).forEach(s => { status[s.code] = s.value; });
            
            // Some Tuya sensors return temp in tenths (118 = 11.8°C)
            let temp = status.va_temperature || status.temp_current || null;
            if (temp !== null) {
              temp = temp > 50 ? temp / 10 : temp;
            }
            
            return {
              name: d.name,
              temp: temp,
              humidity: status.va_humidity || null,
              battery: status.battery_percentage ?? status.va_battery ?? null,
              online: d.online,
            };
          })
          .filter(s => s.online && s.temp !== null)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSensors(tempSensors);
        
        // Lights - UNIQUEMENT Saint-Georges
        const lightCategories = ['dj', 'dd', 'fwd', 'dc', 'xdd', 'fsd', 'fcd', 'tdq', 'kg'];
        const lightDevices = saintGeorgesDevices.filter(d => lightCategories.includes(d.category));
        const onLights = lightDevices.filter(d => 
          (d.status || []).some(s => 
            ['switch_led', 'switch', 'switch_1'].includes(s.code) && s.value === true
          )
        ).length;
        setLights({ on: onLights, total: lightDevices.length });
        
        // Smoke detectors - UNIQUEMENT Saint-Georges
        const smoke = saintGeorgesDevices.filter(d => d.category === 'ywbj');
        setSmokeDetectors(smoke);
        
        // Chargeur VE depuis API dédiée (coûts inclus)
        const charger = saintGeorgesDevices.find(d => d.category === 'qccdz');
        if (chargerRes.status === 'fulfilled' && chargerRes.value.data?.success) {
          const c = chargerRes.value.data.charger;
          setEvCharger({
            id: c.device_id,
            name: c.name,
            online: charger ? charger.online : true,
            workState: c.status,
            isCharging: c.is_charging,
            energy: c.last_charge_kwh || 0,
            lastChargeCost: c.last_charge_cost || 0,
            totalCost: c.total_cost || 0,
          });
        } else if (charger) {
          const status = {};
          (charger.status || []).forEach(s => { status[s.code] = s.value; });
          setEvCharger({
            id: charger.id,
            name: charger.name,
            online: charger.online,
            workState: status.work_state,
            isCharging: status.work_state === 'charging',
            energy: status.charge_energy_once || 0,
            lastChargeCost: null,
            totalCost: null,
          });
        }
      }

      // Viessmann
      if (vicareRes.status === 'fulfilled' && vicareRes.value.data?.success) {
        setHeating(vicareRes.value.data);
      }

      // Solar (Growatt) - L'API retourne directement les données sans wrapper "success"
      if (solarRes.status === 'fulfilled' && solarRes.value.data) {
        const solarData = solarRes.value.data;
        // Si pas d'erreur majeure, utiliser les données
        if (!solarData.error || solarData.today_energy > 0) {
          setSolar(solarData);
        }
      }

      // Enedis
      if (enedisRes.status === 'fulfilled' && enedisRes.value.data?.success) {
        setGrid(enedisRes.value.data);
      }

      // Weather
      if (weatherRes.status === 'fulfilled' && weatherRes.value.data) {
        setWeather(weatherRes.value.data);
      }

      // Mailbox
      if (mailboxRes.status === 'fulfilled' && mailboxRes.value.data) {
        setMailbox(mailboxRes.value.data);
      }

      // Vehicles
      if (vehiclesRes.status === 'fulfilled' && vehiclesRes.value.data) {
        setVehicles(vehiclesRes.value.data);
      }

      // Home Connect
      if (homeConnectRes.status === 'fulfilled' && homeConnectRes.value?.data?.success) {
        setHomeConnect(homeConnectRes.value.data);
      }

      // Ariston
      if (aristonRes.status === 'fulfilled' && aristonRes.value?.data?.success) {
        setAriston(aristonRes.value.data);
      }

      // Build alerts
      const alertsList = [];
      sensors.filter(s => !s.online).forEach(s => alertsList.push({ type: 'warning', message: `${s.name} hors ligne` }));
      sensors.filter(s => s.battery !== null && s.battery < 20).forEach(s => alertsList.push({ type: 'warning', message: `Batterie faible: ${s.name}` }));
      setAlerts(alertsList);

    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const resetMailbox = async () => {
    try { await api.post('/mailbox/reset'); loadData(); } catch (e) { console.error(e); }
  };

  // Data calculations
  const avgTemp = sensors.length > 0 ? sensors.reduce((sum, s) => sum + (s.temp || 0), 0) / sensors.length : null;
  const avgHumidity = sensors.filter(s => s.humidity).length > 0 
    ? sensors.filter(s => s.humidity).reduce((sum, s) => sum + s.humidity, 0) / sensors.filter(s => s.humidity).length 
    : null;
  const solarPower = solar?.current_power || solar?.value || 0;
  
  // Production solaire Growatt - aujourd'hui en kWh
  const solarTodayKwh = solar?.today_energy || 0;
  
  // Données Enedis - consommation d'hier (Linky n'a pas le temps réel)
  const gridYesterdayKwh = grid?.daily?.consumption?.yesterday_kwh || 0;
  const gridPower = grid?.daily?.max_power?.overall_max_power || 0; // Puissance max comme indicateur
  
  // Production solaire injectée (Enedis)
  const solarYesterdayKwh = grid?.daily?.production?.yesterday_kwh || 0;
  
  // Balance énergétique (solaire Growatt temps réel - réseau estimé)
  const solarPowerNow = solar?.value || 0;
  const balance = solarPowerNow - (gridPower / 10); // Estimation
  
  // Données chauffage Viessmann
  const boilerTemp = heating?.temperatures?.boiler;
  const dhwTemp = heating?.temperatures?.dhw;
  const burnerActive = heating?.burner?.active;
  const gasToday = heating?.gas_consumption?.total?.today || 0;

  if (loading) {
    return (
      <Layout title="Centre de Commande">
        <div className="h-full flex items-center justify-center">
          <div className="text-cyan-400 font-mono animate-pulse text-lg">CHARGEMENT SYSTÈMES...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Centre de Commande">
      <div className="h-full flex flex-col p-3" style={{ background: '#050505' }}>
        
        {/* Alerte courrier */}
        {mailbox?.has_mail && (
          <div className="mb-3 p-3 rounded-lg border-2 border-yellow-500 bg-yellow-900/30 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-yellow-400" />
              <div>
                <div className="text-lg font-mono font-bold text-yellow-400">VOUS AVEZ DU COURRIER</div>
                <div className="text-sm text-yellow-300/70 font-mono">{mailbox.detection_count} détection(s)</div>
              </div>
            </div>
            <button onClick={resetMailbox} className="px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-black font-mono font-bold">
              COURRIER RETIRÉ
            </button>
          </div>
        )}
        
        {/* Grid 4 colonnes */}
        <div className="flex-1 grid grid-cols-4 gap-3 overflow-hidden">
          
          {/* COL 1: TEMPÉRATURES */}
          <div className="flex flex-col overflow-hidden">
            <SectionHeader color="#f97316" label="TEMPÉRATURES" onClick={() => navigate('/thermometers')} />
            
            {/* Summary */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <BigDataWidget icon={Thermometer} title="MOY." value={avgTemp?.toFixed(1) || '--'} unit="°C" color="#f97316" />
              <BigDataWidget icon={Droplets} title="HUM." value={avgHumidity?.toFixed(0) || '--'} unit="%" color="#06b6d4" />
            </div>
            
            {/* Weather */}
            {weather && (
              <div className="p-2 rounded-lg bg-blue-900/20 border border-blue-500/30 mb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CloudRain className="w-5 h-5 text-blue-400" />
                    <span className="text-lg font-mono font-bold text-blue-400">{weather.temperature?.toFixed(1)}°C</span>
                  </div>
                  <span className="text-sm text-blue-300/70 font-mono">Extérieur</span>
                </div>
              </div>
            )}
            
            {/* Sensors list */}
            <div className="flex-1 overflow-y-auto space-y-1 pr-1">
              {sensors.slice(0, 12).map((s, i) => (
                <TempRow key={i} {...s} />
              ))}
            </div>
          </div>
          
          {/* COL 2: ÉCLAIRAGE + CHAUFFAGE */}
          <div className="flex flex-col gap-3 overflow-hidden">
            <div>
              <SectionHeader color="#eab308" label="ÉCLAIRAGE" onClick={() => navigate('/lights')} />
              <BigDataWidget 
                icon={Lightbulb} 
                title="LUMIÈRES" 
                value={`${lights.on}/${lights.total}`} 
                unit="" 
                color={lights.on > 0 ? '#eab308' : '#666'}
                subtitle={lights.on > 0 ? 'Allumées' : 'Tout éteint'}
                onClick={() => navigate('/lights')}
              />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button className="p-2 rounded border border-green-500/30 bg-green-900/20 text-green-400 font-mono text-sm font-bold hover:bg-green-900/40">
                  <Power className="w-4 h-4 mx-auto mb-1" /> TOUT ON
                </button>
                <button className="p-2 rounded border border-gray-500/30 bg-gray-900/20 text-gray-400 font-mono text-sm font-bold hover:bg-gray-900/40">
                  <PowerOff className="w-4 h-4 mx-auto mb-1" /> TOUT OFF
                </button>
              </div>
            </div>
            
            <div className="flex-1">
              <SectionHeader color="#f97316" label="CHAUFFAGE" onClick={() => navigate('/heating')} />
              <div className="space-y-2">
                <BigDataWidget 
                  icon={Flame} 
                  title="CHAUDIÈRE" 
                  value={boilerTemp?.toFixed(1) || '--'} 
                  unit="°C" 
                  color={burnerActive ? '#ef4444' : '#f97316'}
                  subtitle={burnerActive ? '🔥 Brûleur actif' : heating?.heating?.active_mode || 'Veille'}
                />
                <BigDataWidget 
                  icon={Droplets} 
                  title="EAU CHAUDE" 
                  value={dhwTemp?.toFixed(1) || '--'} 
                  unit="°C" 
                  color="#06b6d4"
                  subtitle={heating?.dhw?.active ? 'En chauffe' : 'Prêt'}
                />
              </div>
            </div>
          </div>
          
          {/* COL 3: ÉNERGIE + VÉHICULES */}
          <div className="flex flex-col gap-3 overflow-hidden">
            <div>
              <SectionHeader color="#22c55e" label="ÉNERGIE" onClick={() => navigate('/energy')} />
              <div className="grid grid-cols-2 gap-2">
                <BigDataWidget icon={Sun} title="SOLAIRE" value={solarTodayKwh.toFixed(1)} unit="kWh" color="#22c55e" subtitle="aujourd'hui" />
                <BigDataWidget icon={Zap} title="CONSO" value={gridYesterdayKwh.toFixed(1)} unit="kWh" color="#3b82f6" subtitle="hier (Linky)" />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <BigDataWidget 
                  icon={Flame} 
                  title="GAZ" 
                  value={gasToday} 
                  unit="kWh" 
                  color="#f97316"
                  subtitle="aujourd'hui"
                />
                <BigDataWidget 
                  icon={balance >= 0 ? TrendingUp : TrendingDown} 
                  title="BILAN" 
                  value={balance >= 0 ? `+${balance.toFixed(0)}` : balance.toFixed(0)} 
                  unit="W" 
                  color={balance >= 0 ? '#22c55e' : '#ef4444'}
                  subtitle={balance >= 0 ? 'Excédent' : 'Import'}
                />
              </div>
              {/* Installation solaire */}
              <div className="mt-2 p-1.5 rounded border border-green-500/20 bg-green-900/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-mono text-green-400">
                    <Home className="w-3 h-3" />
                    <span className="font-bold">St Georges LUX</span>
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono">3 kWc | {solar?.total_kwh?.toFixed(0) || 0} kWh</div>
                </div>
              </div>
            </div>
            
            <div className="flex-1">
              <SectionHeader color="#3b82f6" label="VÉHICULES" onClick={() => navigate('/vehicles')} />
              <div className="space-y-2">
                {vehicles && (
                  <>
                    <VehicleWidget vehicle={vehicles.mg4} type="mg4" />
                    <VehicleWidget vehicle={vehicles.niu} type="niu" />
                  </>
                )}
                {/* Chargeur VE 7,4 kW */}
                {evCharger && (
                  <div 
                    data-testid="ev-charger-widget"
                    className={`p-2 rounded-lg border transition-all ${evCharger.isCharging ? 'animate-pulse' : ''}`}
                    style={{ 
                      borderColor: evCharger.isCharging ? '#22c55e' : '#3b82f640',
                      background: evCharger.isCharging ? 'rgba(34, 197, 94, 0.15)' : 'rgba(59, 130, 246, 0.08)'
                    }}
                    onClick={() => navigate('/vehicles')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className={`w-5 h-5 ${evCharger.isCharging ? 'text-green-400' : 'text-blue-400'}`} />
                        <div>
                          <div className="text-sm font-mono font-bold text-blue-400">CHARGEUR 7,4 kW</div>
                          <div className="text-xs font-mono text-gray-500">
                            {evCharger.workState === 'charger_free' ? 'DISPONIBLE' : 
                             evCharger.isCharging ? 'EN CHARGE' : 
                             evCharger.workState?.toUpperCase() || 'INCONNU'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {evCharger.lastChargeCost !== null && (
                          <div className="text-sm font-mono font-bold text-yellow-400">{evCharger.lastChargeCost.toFixed(2)}€</div>
                        )}
                        <div className="text-xs font-mono text-gray-500">{evCharger.energy} kWh</div>
                      </div>
                      {!evCharger.online && (
                        <div className="text-xs text-red-400 font-mono">HORS LIGNE</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* COL 4: SÉCURITÉ */}
          <div className="flex flex-col overflow-hidden">
            <SectionHeader color="#ef4444" label="SÉCURITÉ" onClick={() => navigate('/security')} />
            
            {/* Alertes */}
            <div className="p-3 rounded-lg border border-red-500/30 bg-red-900/10 mb-2">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-sm font-mono font-bold text-red-400">{alerts.length} ALERTES</span>
              </div>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {alerts.length > 0 ? (
                  alerts.slice(0, 4).map((a, i) => (
                    <div key={i} className="text-xs font-mono text-red-300/80 truncate">{a.message}</div>
                  ))
                ) : (
                  <div className="text-xs font-mono text-green-400">✓ Aucune alerte</div>
                )}
              </div>
            </div>
            
            {/* Détecteurs fumée */}
            <div className="p-3 rounded-lg border border-orange-500/30 bg-orange-900/10 mb-2">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-400" />
                <span className="text-sm font-mono font-bold text-orange-400">DÉTECTEURS FUMÉE</span>
              </div>
              <div className="mt-1 text-sm font-mono text-gray-400">
                <span className="text-green-400">●</span> {smokeDetectors.filter(d => d.online).length}/{smokeDetectors.length} en ligne
              </div>
            </div>
            
            {/* Brûleur gaz */}
            <div className={`p-3 rounded-lg border ${burnerActive ? 'border-red-500 bg-red-900/30 animate-pulse' : 'border-gray-700 bg-gray-900/30'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Flame className={`w-5 h-5 ${burnerActive ? 'text-red-400' : 'text-gray-500'}`} />
                  <span className="text-sm font-mono font-bold" style={{ color: burnerActive ? '#ef4444' : '#666' }}>BRÛLEUR GAZ</span>
                </div>
                <span className={`text-sm font-mono font-bold ${burnerActive ? 'text-red-400' : 'text-gray-600'}`}>
                  {burnerActive ? 'ACTIF' : 'ARRÊT'}
                </span>
              </div>
            </div>
            
            {/* Placeholders */}
            <div className="flex-1 space-y-2 mt-2 overflow-y-auto">
              {/* Home Connect Appliances */}
              {homeConnect?.appliances?.length > 0 && (
                <div className="p-2 rounded border border-cyan-500/30 bg-cyan-900/10" data-testid="homeconnect-widget">
                  <div className="flex items-center gap-2 text-sm font-mono text-cyan-400 mb-1">
                    <Home className="w-4 h-4" />
                    <span className="font-bold">HOME CONNECT</span>
                  </div>
                  {homeConnect.appliances.slice(0, 3).map((app, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-xs font-mono">
                      <span className="text-gray-300 truncate">{app.name || app.type}</span>
                      <span className={`${app.connected ? 'text-green-400' : 'text-gray-600'}`}>
                        {app.connected ? (app.status?.operationState?.includes('Run') ? 'EN COURS' : 'PRÊT') : 'OFF'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Ariston Water Heater */}
              {ariston?.connected && (
                <div className="p-2 rounded border border-blue-500/30 bg-blue-900/10" data-testid="ariston-widget">
                  <div className="flex items-center gap-2 text-sm font-mono text-blue-400">
                    <Droplets className="w-4 h-4" />
                    <span className="font-bold">ARISTON ECS</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-lg font-mono font-bold text-blue-400">
                      {ariston.current_temp ?? '--'}°
                    </span>
                    <span className="text-xs font-mono text-gray-500">
                      Cible: {ariston.target_temp ?? '--'}°
                    </span>
                  </div>
                </div>
              )}
              
              <div className="p-2 rounded border border-dashed border-cyan-500/30 text-cyan-400/60">
                <div className="flex items-center gap-2 text-sm font-mono"><Waves className="w-4 h-4" /> PISCINE</div>
                <div className="text-xs font-mono text-gray-600">À venir</div>
              </div>
              <div className="p-2 rounded border border-dashed border-purple-500/30 text-purple-400/60">
                <div className="flex items-center gap-2 text-sm font-mono"><MapPin className="w-4 h-4" /> PORTAIL</div>
                <div className="text-xs font-mono text-gray-600">À venir</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
